import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import {
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  Settings,
  CreditCard,
  Headphones,
  Receipt,
  FileCheck,
  PieChart,
  TrendingUp as TrendingUpIcon,
  Layers,
  LayoutDashboard
} from 'lucide-react'
import { toast } from 'react-toastify'
import salesDashboardService from '../../services/salesDashboardService'
import salesPipelineService from '../../services/salesPipelineService'
import { formatCurrency, formatCompactCurrency } from '../../utils/currencyFormat'
import './SalesOverview.css'

/** CRM / pipeline UI lives in Celyntiq — hide duplicates here (do not delete) */
const SHOW_CRM_PIPELINE_UI = false

const Sales = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [dashboardData, setDashboardData] = useState(null)
  const [pipelineSummary, setPipelineSummary] = useState(null)
  const [pipelineLoading, setPipelineLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [chartCurrency, setChartCurrency] = useState('INR')

  // Load dashboard data for overview metrics and masters/transactions counts
  useEffect(() => {
    loadDashboardData()
    if (moduleView === 'overview' && SHOW_CRM_PIPELINE_UI) loadPipelineSummary()
  }, [moduleView])

  useEffect(() => {
    const trends = dashboardData?.revenueTrends || {}
    const codes = dashboardData?.currencies?.length
      ? dashboardData.currencies
      : Object.keys(trends)
    const withRevenue = codes.find((code) => (trends[code] || []).some((month) => Number(month.revenue) > 0))
    if (withRevenue) setChartCurrency(withRevenue)
  }, [dashboardData])

  const loadPipelineSummary = async () => {
    try {
      setPipelineLoading(true)
      const res = await salesPipelineService.getPipelineSummary()
      if (res?.success) setPipelineSummary(res.data)
    } catch (e) {
      console.error('Pipeline summary:', e)
    } finally {
      setPipelineLoading(false)
    }
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await salesDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        // Only show error if response exists but success is false
        // and error wasn't already handled by API interceptor
        if (response.message && !response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      // Don't show duplicate error - API interceptor already handles errors
      // Check if error was already handled by the interceptor
      if (!error._errorHandled) {
        // Only show error if it wasn't handled by interceptor
        if (error.response) {
          // Server responded with error status
          const errorMessage = error.response.data?.message || 'Failed to load dashboard data'
          toast.error(errorMessage)
        } else if (!error.request) {
          // Not a network error, show generic error
          toast.error('Failed to load dashboard data')
        }
        // If error.request exists, it's a network error and interceptor already handled it
      }
    } finally {
      setLoading(false)
    }
  }

  const money = (amount, currency) => formatCurrency(amount || 0, currency || 'INR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const changeLabel = (row) => {
    if (row?.isNew) return 'New'
    if (row?.change === null || row?.change === undefined) return 'No prior month'
    const value = Number(row.change)
    if (Number.isNaN(value)) return ''
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
  }

  const monthRows = dashboardData?.billing?.thisMonth?.length
    ? dashboardData.billing.thisMonth
    : []
  const receivables = dashboardData?.billing?.receivables || []
  const overdueRows = dashboardData?.billing?.overdue || []
  const pipelineRows = dashboardData?.pipelineByCurrency || []
  const chartCodes = dashboardData?.currencies?.length
    ? dashboardData.currencies
    : Object.keys(dashboardData?.revenueTrends || {})
  const activeChart = chartCodes.includes(chartCurrency) ? chartCurrency : (chartCodes[0] || 'INR')
  const chartSeries = dashboardData?.revenueTrends?.[activeChart] || (activeChart === 'INR' ? dashboardData?.revenueTrend : [])
  const metrics = dashboardData?.metrics || {}
  const stageEntries = Object.entries(dashboardData?.dealStages || {}).filter(([, count]) => Number(count) > 0)

  const changeTone = (row) => {
    if (row?.isNew) return 'new'
    const value = Number(row?.change)
    if (!Number.isFinite(value) || value === 0) return 'flat'
    return value > 0 ? 'up' : 'down'
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1>Sales</h1>
          <p style={{textAlign: 'left', margin: 0}}>Approved invoices, collections, and open proposals. INR, USD, and every other currency stay in their own total.</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/sales')} />
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading dashboard data...</p>
            </div>
          ) : (
            <>
              <div className="sales-overview">
                <div className="so-facts">
                  <div className="so-fact">
                    <span>Invoices this month</span>
                    <strong>{metrics.invoicesThisMonth || 0}</strong>
                  </div>
                  <div className="so-fact">
                    <span>Domestic</span>
                    <strong>{metrics.domesticInvoicesThisMonth || 0}</strong>
                  </div>
                  <div className="so-fact">
                    <span>Export</span>
                    <strong>{metrics.exportInvoicesThisMonth || 0}</strong>
                  </div>
                  <div className="so-fact">
                    <span>Overdue invoices</span>
                    <strong>{metrics.overdueCount || 0}</strong>
                  </div>
                  <div className="so-fact">
                    <span>Proposals accepted</span>
                    <strong>{metrics.newDealsClosed || 0}</strong>
                  </div>
                </div>

                <section className="so-section">
                  <div className="so-section-head">
                    <div>
                      <h2>Billed this month</h2>
                      <p>Approved tax invoices, compared with last month. Each currency is totalled on its own.</p>
                    </div>
                  </div>
                  {monthRows.length === 0 ? (
                    <div className="so-empty">No approved tax invoices this month or last month.</div>
                  ) : (
                    <div className="so-currency-grid">
                      {monthRows.map((row) => (
                        <article key={row.currency} className={`so-currency-card so-${row.currency.toLowerCase()}`}>
                          <div className="so-card-top">
                            <span className="so-code">{row.currency}</span>
                            <span className={`so-change so-change-${changeTone(row)}`}>{changeLabel(row)}</span>
                          </div>
                          <div className="so-amount">{money(row.billed, row.currency)}</div>
                          <div className="so-last">Last month {money(row.previousBilled, row.currency)}</div>
                          <div className="so-meta">
                            <span>{row.invoices || 0} invoice{row.invoices === 1 ? '' : 's'}</span>
                            <span>{row.exportInvoices || 0} export</span>
                            <span>Tax {money(row.tax, row.currency)}</span>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>

                <div className="so-split">
                  <section className="so-panel">
                    <div className="so-section-head">
                      <div>
                        <h2>Still to collect</h2>
                        <p>Open balances on billed invoices. Overdue amounts are called out separately.</p>
                      </div>
                    </div>
                    {receivables.length === 0 ? (
                      <div className="so-empty">Nothing outstanding. Billed invoices are collected.</div>
                    ) : (
                      <ul className="so-money-list">
                        {receivables.map((row) => {
                          const late = overdueRows.find((item) => item.currency === row.currency)
                          return (
                            <li key={row.currency}>
                              <span className="so-code">{row.currency}</span>
                              <span className="so-money-figure">{money(row.outstanding, row.currency)}</span>
                              <span className="so-money-note">
                                {row.invoices} open
                                {late ? ` · ${money(late.outstanding, row.currency)} overdue` : ''}
                              </span>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </section>

                  <section className="so-panel">
                    <div className="so-section-head">
                      <div>
                        <h2>Open proposals</h2>
                        <p>Draft, sent, and under review. Values stay in the proposal currency.</p>
                      </div>
                    </div>
                    {pipelineRows.length === 0 ? (
                      <div className="so-empty">No open proposals.</div>
                    ) : (
                      <ul className="so-money-list">
                        {pipelineRows.map((row) => (
                          <li key={row.currency}>
                            <span className="so-code">{row.currency}</span>
                            <span className="so-money-figure">{money(row.value, row.currency)}</span>
                            <span className="so-money-note">{row.count} proposal{row.count === 1 ? '' : 's'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>

              {/* Pipeline Overview — CRM (hidden; use Celyntiq) */}
              {SHOW_CRM_PIPELINE_UI && (
              <>
              <div className="section-header" style={{ marginTop: '24px', marginBottom: '12px' }}>
                <h3 className="mb-0">Pipeline Overview</h3>
              </div>
              <div className="dashboard-metrics-grid">
                <div className="metric-card revenue">
                  <div className="metric-header">
                    <div className="metric-icon"><DollarSign size={16} /></div>
                  </div>
                  <div className="metric-value">{pipelineLoading ? '...' : formatCurrency(pipelineSummary?.totalPipeline ?? 0)}</div>
                  <div className="metric-title">Total Pipeline</div>
                  <div className="metric-subtitle">Open opportunities</div>
                </div>
                <div className="metric-card customers">
                  <div className="metric-header">
                    <div className="metric-icon"><Target size={16} /></div>
                  </div>
                  <div className="metric-value">{pipelineLoading ? '...' : formatCurrency(pipelineSummary?.weightedPipeline ?? 0)}</div>
                  <div className="metric-title">Weighted Pipeline</div>
                  <div className="metric-subtitle">Value × probability</div>
                </div>
                <div className="metric-card churn">
                  <div className="metric-header">
                    <div className="metric-icon"><AlertTriangle size={16} /></div>
                    {(pipelineSummary?.overdueFollowUps || 0) > 0 && (
                      <span className="badge bg-danger ms-1">{pipelineSummary.overdueFollowUps}</span>
                    )}
                  </div>
                  <div className="metric-value">{pipelineLoading ? '...' : (pipelineSummary?.overdueFollowUps ?? 0)}</div>
                  <div className="metric-title">Overdue Follow-ups</div>
                  <div className="metric-subtitle">Need attention</div>
                </div>
              </div>

              {pipelineSummary?.byStage?.length > 0 && (
                <div className="dashboard-chart-card revenue-chart" style={{ marginTop: '16px' }}>
                  <div className="chart-header">
                    <h3>Stage Distribution</h3>
                    <p>Pipeline by stage</p>
                  </div>
                  <div className="chart-content">
                    <div className="health-distribution">
                      {pipelineSummary.byStage.map((s, i) => {
                        const total = pipelineSummary.byStage.reduce((a, x) => a + (x.total || 0), 0)
                        const width = total > 0 ? ((s.total || 0) / total) * 100 : 0
                        return (
                          <div key={s.stageId || i} className="health-segment">
                            <div className="segment-bar" style={{ width: `${width}%` }} />
                            <div className="segment-info">
                              <span className="segment-label">{s.stageName}</span>
                              <span className="segment-count">{formatCurrency(s.total)}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {pipelineSummary?.next7DaysFollowUps?.length > 0 && (
                <div className="dashboard-action-section" style={{ marginTop: '24px' }}>
                  <div className="dashboard-action-items">
                    <div className="section-header">
                      <h3>Follow-ups Due Next 7 Days</h3>
                    </div>
                    <div className="action-list">
                      {pipelineSummary.next7DaysFollowUps.map((opp, idx) => (
                        <div key={opp._id || idx} className="action-item medium" style={{ cursor: 'pointer' }} onClick={() => navigate('/sales/opportunities')}>
                          <div className="action-icon"><Calendar size={16} /></div>
                          <div className="action-content">
                            <div className="action-title">{opp.name}</div>
                            <div className="action-description">{opp.customerId?.customerName || '—'} · {opp.stageId?.name || '—'}</div>
                          </div>
                          <div className="action-time">{new Date(opp.nextFollowUpDate).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              </>
              )}

              <div className="so-split so-lower">
                <section className="so-panel">
                  <div className="so-section-head">
                    <div>
                      <h2>Needs a decision</h2>
                      <p>Drafts to approve, and invoices due within seven days.</p>
                    </div>
                  </div>
                  {dashboardData?.actionItems?.length ? (
                    <ul className="so-work-list">
                      {dashboardData.actionItems.map((item, index) => (
                        <li key={item.id || index} className={`so-work is-${item.kind || 'due'}`}>
                          <span className="so-work-mark" aria-hidden="true">
                            <AlertTriangle size={14} />
                          </span>
                          <div className="so-work-copy">
                            <strong>{item.title}</strong>
                            <span>{item.description}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="so-empty">Nothing waiting on you this week.</div>
                  )}
                </section>

                <section className="so-panel">
                  <div className="so-section-head">
                    <div>
                      <h2>Collections</h2>
                      <p>Open invoices, oldest due date first. Each amount stays in its currency.</p>
                    </div>
                    <button type="button" className="so-text-link" onClick={() => navigate('/sales/reports/tax-invoice')}>
                      Invoice report
                    </button>
                  </div>
                  {dashboardData?.collections?.length ? (
                    <ul className="so-work-list">
                      {dashboardData.collections.map((row) => (
                        <li key={row.id} className={row.daysOverdue ? 'so-work is-overdue' : 'so-work'}>
                          <div className="so-work-copy">
                            <strong>{row.customer}</strong>
                            <span>{row.number} · {row.currency}</span>
                          </div>
                          <div className="so-work-money">
                            <strong>{money(row.outstanding, row.currency)}</strong>
                            <span>{row.daysOverdue ? `${row.daysOverdue} days overdue` : 'Not yet due'}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="so-empty">Every billed invoice is collected.</div>
                  )}
                </section>
              </div>
              </div>

              <div className="dashboard-charts-section">
                <div className="dashboard-chart-card revenue-chart">
                  <div className="chart-header so-chart-header">
                    <div>
                      <h3>Billed by month</h3>
                      <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`} · {activeChart} only</p>
                    </div>
                    {chartCodes.length > 0 && (
                      <div className="so-currency-switch" role="tablist" aria-label="Chart currency">
                        {chartCodes.map((code) => (
                          <button
                            key={code}
                            type="button"
                            role="tab"
                            aria-selected={code === activeChart}
                            className={code === activeChart ? 'is-active' : ''}
                            onClick={() => setChartCurrency(code)}
                          >
                            {code}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="chart-content">
                    <FyMonthlyBarChart
                      series={chartSeries}
                      valueKey="revenue"
                      formatValue={(value) => formatCompactCurrency(value, activeChart)}
                      formatTooltip={(value) => money(value, activeChart)}
                      emptyMessage={`No ${activeChart} invoices in this financial year`}
                    />
                  </div>
                </div>

                {stageEntries.length > 0 && (
                  <div className="dashboard-chart-card customer-health">
                    <div className="chart-header">
                      <h3>Proposal status</h3>
                      <p>Count of proposals, not a combined value</p>
                    </div>
                    <div className="chart-content">
                      <div className="health-distribution">
                        {stageEntries.map(([stage, count]) => {
                          const total = stageEntries.reduce((sum, [, value]) => sum + Number(value), 0)
                          const width = total > 0 ? (Number(count) / total) * 100 : 0
                          return (
                            <div key={stage} className="health-segment">
                              <div className="segment-bar" style={{ width: `${width}%` }} />
                              <div className="segment-info">
                                <span className="segment-label">{stage}</span>
                                <span className="segment-count">{count}</span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="sales-masters-content">
            <div className="sales-masters-grid">
              <div className="sales-master-card clickable" onClick={() => navigate('/sales/sac-master')}>
                <div className="sales-master-icon">
                  <Settings size={24} />
                </div>
                <div className="sales-master-content">
                  <h3>SAC Master</h3>
                  <p>Services Accounting Code master data management</p>
                  <div className="sales-master-count">{dashboardData?.masterData?.sacCodes || 0} Active</div>
                </div>
              </div>
              <div className="sales-master-card clickable" onClick={() => navigate('/sales/service-master')}>
                <div className="sales-master-icon">
                  <Briefcase size={24} />
                </div>
                <div className="sales-master-content">
                  <h3>Service Master</h3>
                  <p>Service catalog and configuration management</p>
                  <div className="sales-master-count">{dashboardData?.masterData?.services || 0} Services</div>
                </div>
              </div>
              <div className="sales-master-card clickable" onClick={() => navigate('/sales/customer-master')}>
                <div className="sales-master-icon">
                  <Users size={24} />
                </div>
                <div className="sales-master-content">
                  <h3>Customer Master</h3>
                  <p>Business-to-business customer master data</p>
                  <div className="sales-master-count">{dashboardData?.masterData?.customers || 0} Customers</div>
                </div>
              </div>
              <div className="sales-master-card clickable" onClick={() => navigate('/sales/payment-terms-master')}>
                <div className="sales-master-icon">
                  <CreditCard size={24} />
                </div>
                <div className="sales-master-content">
                  <h3>Payment Terms Master</h3>
                  <p>Payment terms and conditions configuration</p>
                  <div className="sales-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.paymentTerms ?? 0} Terms`}
                  </div>
                </div>
              </div>
              {/* Sales Stage Master — CRM pipeline (hidden; use Celyntiq) */}
              {SHOW_CRM_PIPELINE_UI && (
              <div className="sales-master-card clickable" onClick={() => navigate('/sales/stage-master')}>
                <div className="sales-master-icon">
                  <Layers size={24} />
                </div>
                <div className="sales-master-content">
                  <h3>Sales Stage Master</h3>
                  <p>Pipeline stages and default win probability</p>
                  <div className="sales-master-count">Pipeline stages</div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="sales-transactions-content">
            <div className="sales-transactions-grid">
              <ModuleOverviewCard
                prefix="sales-transaction"
                moduleName="Sales"
                showArrow={false}
                onClick={() => navigate('/sales?view=overview')}
              />
              <div 
                className="sales-transaction-card clickable"
                onClick={() => navigate('/sales/sales-quotation')}
              >
                <div className="sales-transaction-icon">
                  <FileText size={24} />
                </div>
                <div className="sales-transaction-content">
                  <h3>Sales Quotation</h3>
                  <p>Create and manage sales quotations</p>
                  <div className="sales-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.quotationsActive ?? 0} Active`}
                  </div>
                </div>
              </div>
              <div 
                className="sales-transaction-card clickable"
                onClick={() => navigate('/sales/proforma-invoice')}
              >
                <div className="sales-transaction-icon">
                  <FileCheck size={24} />
                </div>
                <div className="sales-transaction-content">
                  <h3>Proforma Invoice</h3>
                  <p>Create preliminary invoices for approval</p>
                  <div className="sales-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.proformaPending ?? 0} Pending`}
                  </div>
                </div>
              </div>
              <div 
                className="sales-transaction-card clickable"
                onClick={() => navigate('/sales/tax-invoice')}
              >
                <div className="sales-transaction-icon">
                  <Receipt size={24} />
                </div>
                <div className="sales-transaction-content">
                  <h3>Tax Invoice</h3>
                  <p>Generate and manage tax-compliant invoices</p>
                  <div className="sales-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.taxInvoicesGenerated ?? 0} Generated`}
                  </div>
                </div>
              </div>
              {/* Opportunities — CRM pipeline (hidden; use Celyntiq) */}
              {SHOW_CRM_PIPELINE_UI && (
              <div 
                className="sales-transaction-card clickable"
                onClick={() => navigate('/sales/opportunities')}
              >
                <div className="sales-transaction-icon">
                  <Target size={24} />
                </div>
                <div className="sales-transaction-content">
                  <h3>Opportunities</h3>
                  <p>Sales pipeline and opportunity management</p>
                  <div className="sales-transaction-count">Pipeline</div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="sales-reports-content">
            <div className="sales-reports-grid">
              <div
                className="sales-report-card clickable"
                onClick={() => navigate('/sales/reports/overview')}
                style={{ cursor: 'pointer' }}
              >
                <div className="sales-report-icon">
                  <LayoutDashboard size={24} />
                </div>
                <div className="sales-report-content">
                  <h3>Overview</h3>
                  <p>MTD / YTD invoicing, GST and collections with FY charts</p>
                  <div className="sales-report-status">Live data</div>
                </div>
              </div>
              <div 
                className="sales-report-card" 
                onClick={() => navigate('/sales/reports/tax-invoice')}
                style={{ cursor: 'pointer' }}
              >
                <div className="sales-report-icon">
                  <Receipt size={24} />
                </div>
                <div className="sales-report-content">
                  <h3>Tax Invoice</h3>
                  <p>Tax invoice reports and compliance tracking</p>
                  <div className="sales-report-status">Updated Today</div>
                </div>
              </div>
              <div 
                className="sales-report-card" 
                onClick={() => navigate('/sales/reports/sales-register')}
                style={{ cursor: 'pointer' }}
              >
                <div className="sales-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="sales-report-content">
                  <h3>Sales Register</h3>
                  <p>Comprehensive sales register and transaction reports</p>
                  <div className="sales-report-status">Updated Today</div>
                </div>
              </div>
              <div 
                className="sales-report-card clickable"
                onClick={() => navigate('/sales/reports/proforma-invoice')}
                style={{ cursor: 'pointer' }}
              >
                <div className="sales-report-icon">
                  <FileCheck size={24} />
                </div>
                <div className="sales-report-content">
                  <h3>Proforma Invoice</h3>
                  <p>Proforma invoice tracking and approval reports</p>
                  <div className="sales-report-status">Updated Today</div>
                </div>
              </div>
              <div 
                className="sales-report-card" 
                onClick={() => navigate('/sales/reports/sales-quotation')}
                style={{ cursor: 'pointer' }}
              >
                <div className="sales-report-icon">
                  <FileText size={24} />
                </div>
                <div className="sales-report-content">
                  <h3>Sales Quotation</h3>
                  <p>Sales quotation reports and tracking</p>
                  <div className="sales-report-status">Updated Today</div>
                </div>
              </div>
              {/* Pipeline Report — CRM (hidden; use Celyntiq) */}
              {SHOW_CRM_PIPELINE_UI && (
              <div 
                className="sales-report-card" 
                onClick={() => navigate('/sales/reports/pipeline')}
                style={{ cursor: 'pointer' }}
              >
                <div className="sales-report-icon">
                  <Target size={24} />
                </div>
                <div className="sales-report-content">
                  <h3>Pipeline Report</h3>
                  <p>Pipeline by stage, win/loss, owner performance</p>
                  <div className="sales-report-status">Pipeline analytics</div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Sales
